import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    map,
    of,
    shareReplay,
    switchMap,
} from 'rxjs';
import {DateTimePipe, NumberPipe} from '@brunata-angular/ui';
import {MeterService} from '../../meter.service';
import {
    AppValidators,
    FeatureFlagService,
    filterNulls,
    fromControlWithDebounceTime,
    TranslatePipe,
} from '@brunata-angular/util';
import {toSignal} from '@angular/core/rxjs-interop';

type DateForm = {
    date: FormControl<DateTime | null>;
};

type Meter = {
    meterId: string;
    meterNo: string;
    dismountedDate: DateTime | null;
};

// The readings endpoint returns a meter only for days that start before its dismount time.
function latestReadingDate(meter: Meter | null): DateTime {
    const today = DateTime.now().startOf('day');
    return meter?.dismountedDate == null
        ? today
        : DateTime.min(today, meter.dismountedDate.minus({milliseconds: 1}).startOf('day'));
}

@Component({
    selector: 'app-readings',
    templateUrl: './readings.component.html',
    styleUrl: './readings.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        DateTimePipe,
        InputDateGroupComponent,
        ReactiveFormsModule,
        NumberPipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReadingsComponent {
    @Input({required: true}) set meter(value: Meter) {
        this.meterSubject.next(value);
        // No event: meterAndDate$ already starts the new meter on this date.
        this.formGroup.controls.date.setValue(this.maxDate(), {emitEvent: false});
    }
    @Input({required: true}) set buildingNo(value: string) {
        this.buildingNoSubject.next(value);
    }

    private readonly meterSubject = new BehaviorSubject<Meter | null>(null);
    private readonly buildingNoSubject = new BehaviorSubject<string | null>(null);
    private readonly meter$ = this.meterSubject.pipe(filterNulls());
    private readonly buildingNo$ = this.buildingNoSubject.pipe(filterNulls());
    private readonly featureFlagService = inject(FeatureFlagService);

    protected readonly deviceDetailsEnabled = toSignal(
        this.featureFlagService.isFeatureEnabled$('agi-20301-device-details'),
        {initialValue: false},
    );

    protected readonly maxDate = toSignal(this.meterSubject.pipe(map(latestReadingDate)), {
        requireSync: true,
    });

    formGroup: FormGroup<DateForm> = this.formBuilder.group({
        date: new FormControl<DateTime>(this.maxDate(), [
            Validators.required,
            (control): ValidationErrors | null => AppValidators.maxDate(this.maxDate())(control),
        ]),
    });

    // Meter and date travel together, so a request never pairs a meter with the previous meter's date.
    private readonly meterAndDate$ = this.meter$.pipe(
        switchMap(meter =>
            fromControlWithDebounceTime(
                this.formGroup.controls.date,
                latestReadingDate(meter),
            ).pipe(
                filterNulls(),
                map(date => ({meter, date})),
            ),
        ),
        shareReplay({bufferSize: 1, refCount: true}),
    );

    readonly allowMoveDateForward$ = this.meterAndDate$.pipe(
        map(({date}) => this.isBeforeMaxDate(date)),
    );
    readonly view$ = combineLatest([this.meterAndDate$, this.buildingNo$]).pipe(
        distinctUntilChanged(),
        switchMap(([{meter, date}, buildingNo]) =>
            this.meterService.getReadings(buildingNo, meter.meterId, date).pipe(
                map(readings => ({
                    readings: readings,
                    showSerialNo: readings.some(reading => reading.meter.meterNo !== meter.meterNo),
                })),
                catchError(() => of({readings: [], showSerialNo: false})),
            ),
        ),
    );

    constructor(
        private formBuilder: FormBuilder,
        private meterService: MeterService,
    ) {}

    moveOneDayForward(): void {
        const date = this.formGroup.value.date;
        if (date instanceof DateTime && this.isBeforeMaxDate(date)) {
            this.formGroup.patchValue({date: date.plus({days: 1})});
        }
    }

    moveOneDayBack(): void {
        this.formGroup.patchValue({date: this.formGroup.getRawValue().date?.minus({days: 1})});
    }

    private isBeforeMaxDate(date: DateTime): boolean {
        return date.startOf('day') < this.maxDate();
    }
}
